import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { jwtDecode } from 'jwt-decode';
import { authService } from '../services/authService';

interface TierLimits {
  maxIndustryLists: number;
  maxCustomSymbols: number;
  customWatchlistEnabled: boolean;
  hourlyEmails: boolean;
  dailyReport: boolean;
  stockDailyReport: boolean;
}

interface User {
  id: string;
  email: string;
  emailVerified: boolean;
  hourlyEmailsEnabled: boolean;
  dailyReportEnabled: boolean;
  stockDailyReportEnabled: boolean;
  disclaimerAccepted: boolean;
  riskTolerance?: string;
  twoFactorEnabled: boolean;
  subscriptionTier: string;
  subscriptionStatus: string;
  effectiveTier: string;
  tierLimits: TierLimits;
  hasComplimentaryAccess: boolean;
  selectedLists: string[];
  customWatchlist: string[];
  createdAt: string;
  lastLogin?: string;
  algoTradingEnabled: boolean;
  algoEngineLimit: number;
  optionsAutoTradingEnabled?: boolean;
  spxSignalsEnabled: boolean;
  spySignalsEnabled: boolean;
  qqqSignalsEnabled: boolean;
  iwmSignalsEnabled: boolean;
  esSignalsEnabled: boolean;
  mesSignalsEnabled: boolean;
  gcSignalsEnabled: boolean;
  clSignalsEnabled: boolean;
  indexSignalsEnabled: boolean;
  subscriptions?: Record<string, any>;
  // Notches umbrella IA - entitlement string list (post 2026-05-17).
  // Read via hasSubscription(user, key) from frontend/src/lib/entitlements.
  // Pure array-includes; no fallback to the flat flags above. Migration
  // script seeds for existing users.
  entitlements?: string[];
  activeProducts?: string[];
  showV1Signals?: boolean;
  notchesEnabled?: boolean;
  notchesPreset?: 'conservative' | 'balanced' | 'aggressive';
  smsNotchesEnabled?: boolean;
  emailNotchesEnabled?: boolean;
  // TastyTrade fields
  tastyTradeConnected?: boolean;
  tastyTradeAccountNumber?: string;
  tastyTradeConnections?: Array<{
    connectionId: string;
    label?: string;
    accountNumber: string;
    accountNumberMasked?: string;
    accountType?: string;
    createdAt?: string;
  }>;
  // SMS notification fields
  phone?: string;
  phoneVerified?: boolean;
  smsStockSignals?: boolean;
  smsSpxSignals?: boolean;
  smsSpySignals?: boolean;
  smsEsSignals?: boolean;
  smsMesSignals?: boolean;
  smsOptionsTrades?: boolean;
  smsAlgoAlerts?: boolean;
  smsIndexSignals?: boolean;
  tradingSnapshotEnabled?: boolean;
  bbProgramEnabled?: boolean;
  // Trial & pricing fields
  trialStartDate?: string;
  trialEndDate?: string;
  refundWindowEndsAt?: string;
  refundEligibleAfter?: string;
  introDiscountEndsAt?: string;
  subscriptionPlanType?: string;
  subscriptionAccessUntil?: string;
  refundProcessed?: boolean;
  isInTrialPeriod?: boolean;
  canRequestRefund?: boolean;
  daysRemainingInTrial?: number;
  daysRemainingForRefund?: number;
  daysUntilRefundEligible?: number;
  // Stripe billing fields (May 2026 migration)
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  stripeSubscriptionStatus?: string;
  // Calendar-notches trade-insights feature flag (independent of Premier
  // tier -- see api/auth/src/notchesGetCalendarRead.js gate). Frontend uses
  // this to conditionally show the "Calendar Read" nav link; the backend
  // read API is the real gate.
  calendarNotchesEnabled?: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  verify2FA: (code: string, preAuthToken: string) => Promise<void>;
  logout: () => void;
  verifyEmail: (code: string) => Promise<void>;
  updateProfile: (updates: Partial<User>) => Promise<void>;
  refreshUser: () => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const init = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        try {
          const decoded = jwtDecode<any>(storedToken);
          if (decoded.exp * 1000 > Date.now()) {
            setToken(storedToken);
            await loadUser(storedToken);
          } else {
            localStorage.removeItem('token');
          }
        } catch (error) {
          console.error('Invalid token:', error);
          localStorage.removeItem('token');
        }
      }
      setLoading(false);
    };
    init();
  }, []);

  const loadUser = async (authToken: string) => {
    try {
      const userData = await authService.getCurrentUser(authToken);
      setUser(userData);
    } catch (error) {
      console.error('Failed to load user:', error);
      logout();
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await authService.login(email, password);

      // 2FA required — throw a special error the Login component handles
      if (response.requires2FA) {
        const err: any = new Error('TWO_FACTOR_REQUIRED');
        err.requires2FA = true;
        err.preAuthToken = response.preAuthToken;
        throw err;
      }

      const { token: authToken, user: userData } = response;
      localStorage.setItem('token', authToken);
      setToken(authToken);
      setUser(userData);
      loadUser(authToken);
    } catch (error: any) {
      if (error.requires2FA) throw error;
      throw new Error(error.response?.data?.error || error.response?.data?.message || 'Login failed');
    }
  };

  const verify2FA = async (code: string, preAuthToken: string) => {
    try {
      const response = await authService.verify2FA(code, preAuthToken);
      const { token: authToken } = response;
      localStorage.setItem('token', authToken);
      setToken(authToken);
      await loadUser(authToken);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Verification failed');
    }
  };

  const register = async (email: string, password: string) => {
    try {
      const response = await authService.register(email, password);
      const { token: authToken, user: userData } = response;

      localStorage.setItem('token', authToken);
      setToken(authToken);
      setUser(userData);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Registration failed');
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const verifyEmail = async (code: string) => {
    if (!token) throw new Error('Not authenticated');

    try {
      const response = await authService.verifyEmail(code, token);
      setUser(response.user);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Verification failed');
    }
  };

  const updateProfile = async (updates: Partial<User>) => {
    if (!token) throw new Error('Not authenticated');

    try {
      const response = await authService.updateProfile(updates, token);
      setUser(response.user);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Update failed');
    }
  };

  // Re-fetch the current user record from the API. Used after side-effects that
  // change user state outside the normal login flow (e.g. webhook-driven
  // subscription activation, profile updates from other tabs).
  const refreshUser = async () => {
    if (!token) return;
    await loadUser(token);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        login,
        register,
        verify2FA,
        logout,
        verifyEmail,
        updateProfile,
        refreshUser,
        isAuthenticated: !!user && !!token,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};